import React, { useEffect, useMemo, useState } from 'react';
import {
  SEVERITY_BG,
  SEVERITY_COLORS,
  SEVERITY_LABELS,
  SEVERITY_ORDER,
} from '../utils/symptomHelpers';

function isRecent(days, dateValue) {
  const cut = new Date();
  cut.setDate(cut.getDate() - days);
  return new Date(dateValue) >= cut;
}

function formatDateTime(dateValue) {
  return new Date(dateValue).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function initials(name) {
  return String(name || 'User')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
}

export default function ClinicianDashboard({ user, userProfile }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const username = user?.username || '';

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!username) {
        if (mounted) setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const res = await fetch(
          `https://toxsee.onrender.com/api/clinician/patients/${encodeURIComponent(username)}`
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.error || 'Failed to load assigned patients');
        }

        if (mounted) {
          setPatients(Array.isArray(data.patients) ? data.patients : []);
        }
      } catch (err) {
        if (mounted) {
          setError(err?.message || 'Unable to load assigned patients.');
          setPatients([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [username]);

  const stats = useMemo(() => {
    const allSymptoms = patients.flatMap(p => p.symptoms || []);
    const last7Days = allSymptoms.filter(s => isRecent(7, s.loggedAt));
    const severeOrWorse = last7Days.filter(
      s => SEVERITY_ORDER[s.severity] >= SEVERITY_ORDER.severe
    );

    const mostCommonSymptom = (() => {
      const counter = {};
      for (const s of last7Days) {
        counter[s.name] = (counter[s.name] || 0) + 1;
      }
      const sorted = Object.entries(counter).sort((a, b) => b[1] - a[1]);
      return sorted.length > 0 ? `${sorted[0][0]} (${sorted[0][1]})` : 'N/A';
    })();

    return {
      patientCount: patients.length,
      total: allSymptoms.length,
      weekly: last7Days.length,
      severeWeekly: severeOrWorse.length,
      mostCommonSymptom,
      needsAttention: severeOrWorse.length > 0,
    };
  }, [patients]);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px' }}>
        Clinician View
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '18px' }}>
        Assigned patient symptom data for {user?.name || username || 'current clinician'}
      </p>

      {error && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #fecaca',
            background: '#fef2f2',
            color: '#991b1b',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        <div style={{ background: 'white', border: '1px solid var(--gray-border)', borderRadius: '10px', padding: '12px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Assigned patients</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a' }}>{loading ? '...' : stats.patientCount}</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--gray-border)', borderRadius: '10px', padding: '12px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Total entries</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a' }}>{loading ? '...' : stats.total}</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--gray-border)', borderRadius: '10px', padding: '12px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Last 7 days</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a' }}>{loading ? '...' : stats.weekly}</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--gray-border)', borderRadius: '10px', padding: '12px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Severe+ (7d)</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: stats.severeWeekly > 0 ? '#E24B4A' : '#1a1a1a' }}>
            {loading ? '...' : stats.severeWeekly}
          </div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--gray-border)', borderRadius: '10px', padding: '12px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Most common (7d)</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', marginTop: '6px' }}>
            {loading ? '...' : stats.mostCommonSymptom}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {loading ? (
          <div style={{ padding: '24px', color: 'var(--text-muted)', fontSize: '14px', background: 'white', borderRadius: '12px', border: '1px solid var(--gray-border)' }}>
            Loading assigned patients...
          </div>
        ) : patients.length === 0 ? (
          <div style={{ padding: '24px', color: 'var(--text-muted)', fontSize: '14px', background: 'white', borderRadius: '12px', border: '1px solid var(--gray-border)' }}>
            No assigned patients found. Assign patients using the clinician assignment API.
          </div>
        ) : (
          patients.map((patient) => {
            const patientSymptoms = Array.isArray(patient.symptoms) ? patient.symptoms : [];
            const recentSymptoms = patientSymptoms.slice(0, 8);
            const needsAttention = patientSymptoms.some(
              s => isRecent(7, s.loggedAt) && SEVERITY_ORDER[s.severity] >= SEVERITY_ORDER.severe
            );

            return (
              <div
                key={patient.username}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid var(--gray-border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--gray-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'var(--teal-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      fontSize: '13px',
                      color: 'var(--teal-dark)',
                    }}
                  >
                    {initials(patient.name || patient.username)}
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>{patient.name || patient.username}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {patient.profile?.cancerType || 'Cancer not set'} · {patient.profile?.diseaseStage || 'Stage not set'}
                    </div>
                  </div>
                  {needsAttention && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        background: '#fef2f2',
                        color: '#E24B4A',
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: '20px',
                      }}
                    >
                      ⚠ Needs attention
                    </span>
                  )}
                </div>

                {recentSymptoms.length === 0 ? (
                  <div style={{ padding: '16px 20px', color: 'var(--text-muted)', fontSize: '13px' }}>
                    No symptom logs yet.
                  </div>
                ) : (
                  <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {recentSymptoms.map((s, i) => (
                      <div
                        key={s._id || i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          background: SEVERITY_BG[s.severity] || '#f9fafb',
                        }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: SEVERITY_COLORS[s.severity] || '#6b7280',
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontSize: '14px', flex: 1 }}>{s.name}</span>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: SEVERITY_COLORS[s.severity] || '#374151',
                            background: 'white',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            border: `1px solid ${SEVERITY_COLORS[s.severity] || '#cbd5e1'}`,
                          }}
                        >
                          {SEVERITY_LABELS[s.severity] || 'Unknown'}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {formatDateTime(s.loggedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div
        style={{
          marginTop: '24px',
          padding: '12px 16px',
          background: 'white',
          borderRadius: '10px',
          border: '1px solid var(--gray-border)',
          fontSize: '12px',
          color: 'var(--text-muted)',
        }}
      >
        This clinician view aggregates symptom tracker entries across all patients assigned to this clinician. Severe and very severe entries in the last 7 days are highlighted for review.
      </div>
    </div>
  );
}